import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { SelectableImageList } from "../../components";
import { faFolder } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEntitlement } from "../../contexts";
import { Button, Checkbox, Icon } from "../../components/ui";

import "./folder_list.scss";

// Folder files arrive as bare ids from GET /api/products/{id}/folders (and as
// {id} objects from older callers) — accept both.
const fileIdOf = (file) => (file && typeof file === "object" ? file.id : file);
const hasId = (list, id) => list.some((x) => String(x) === String(id));

const FolderList = ({ setImages, productId }) => {
  const { baseProductId } = useParams();
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [checkedFolders, setCheckedFolders] = useState(new Set());
  const [checkedImages, setCheckedImages] = useState(new Set());
  // Reuse listing images (Feature 12): the whole folder window appears only
  // when the store's plan includes it — gated here, at the component itself,
  // so every mount point (listing form tray + product images form) follows
  // the switch without each having to know about it.
  const canReuseImages = useEntitlement("reuse_listing_images");
  // Ids THIS picker put into the gallery. The folder list includes the
  // listing's OWN folder, so ticking it used to append a second copy of
  // images the listing already had — a seed of listing 3642's duplicate
  // rows. Already-present ids are now skipped, and unticking removes only
  // what the picker itself added, never an image the listing already had.
  const addedByPicker = useRef(new Set());

  const fetchData = () => {
    const id = productId ?? baseProductId;
    axios.get("/api/products/" + id + "/folders").then((res) => {
      setFolders(res.data);
    });
  };

  useEffect(() => {
    if (!canReuseImages) return;
    fetchData();
  }, [canReuseImages]);

  const openFolder = (folder) => {
    setSelectedFolder(folder);
    setSelectedFiles(folder.files || []);
  };

  const closeFolder = () => {
    setSelectedFolder(null);
    setSelectedFiles([]);
  };

  // Append ids not already in the gallery (order kept, no repeats).
  const addIds = (prev, ids) => {
    const next = [...(prev || [])];
    ids.forEach((id) => {
      if (!hasId(next, id)) {
        next.push(id);
        addedByPicker.current.add(String(id));
      }
    });
    return next;
  };

  // Remove ids this picker added — never an image the gallery already had
  // before the picker touched it. The set to drop is taken OUTSIDE the state
  // updater so a re-invoked updater (StrictMode) still sees it.
  const removeAddedIds = (ids) => {
    const drop = new Set(
      ids.map(String).filter((key) => addedByPicker.current.has(key))
    );
    drop.forEach((key) => addedByPicker.current.delete(key));
    return (prev) => (prev || []).filter((x) => !drop.has(String(x)));
  };

  const onChange = (e, files, folderId) => {
    // Local images state holds file IDs (numbers) — GalleryUpload's initialState
    // calls getFileUrl(f) on each entry, which expects an ID, not a file object.
    // Passing file objects produced URLs like "/api/files/[object Object]" and
    // the new images silently never rendered in the gallery.
    const fileIds = (files || []).map(fileIdOf).filter((id) => id != null);
    if (e.target.checked) {
      setImages((prev) => addIds(prev, fileIds));
      setCheckedFolders((prev) => new Set([...prev, folderId]));
      fileIds.forEach((id) => {
        setCheckedImages((prev) => new Set([...prev, id]));
      });
    } else {
      setImages(removeAddedIds(fileIds));
      setCheckedFolders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
      fileIds.forEach((id) => {
        setCheckedImages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      });
    }
  };

  const onSelectImage = (e, fileId) => {
    if (e.target.checked) {
      setImages((prev) => addIds(prev, [fileId]));
      setCheckedImages((prev) => new Set([...prev, fileId]));
    } else {
      setImages(removeAddedIds([fileId]));
      setCheckedImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  if (!canReuseImages) {
    return null;
  }

  if (folders && folders.length === 0) {
    return;
  }

  return (
    <div className="folder-container overflow-y">
      {!selectedFolder && (
        <div className="folder-list">
          <table className="table table-striped">
            <tbody>
              {folders.map((folder) => (
                <tr
                  key={folder.id}
                  className="folder-list__row"
                  onClick={() => openFolder(folder)}
                >
                  {/* The row opens the folder; the checkbox cell must not. The
                      stop sits on the cell because ui Checkbox is a <label>, and
                      a click on it bubbles to the row before the input fires. */}
                  <td className="folder-list__check text-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      name="folder"
                      aria-label={`Use every image in ${folder.name}`}
                      checked={checkedFolders.has(folder.id)}
                      onChange={(_, e) => onChange(e, folder.files, folder.id)}
                    />
                  </td>
                  <td className="folder-list__name">
                    <span className="folder-list__icon" aria-hidden="true">
                      <FontAwesomeIcon
                        icon={faFolder}
                        className="text-primary me-3"
                        size="lg"
                      />
                    </span>
                    {folder.name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedFolder && (
        <div className="folder-detail">
          <div className="folder-detail__header">
            <Button
              variant="ghost"
              size="sm"
              data-btn="folder-cancel"
              leadingIcon={<Icon name="ArrowLeft" size="sm" />}
              onClick={closeFolder}
            >
              Back
            </Button>
            <span className="folder-detail__title">{selectedFolder.name}</span>
          </div>

          {selectedFiles.length > 0 ? (
            <SelectableImageList
              files={selectedFiles}
              onChange={onSelectImage}
              checkedImages={checkedImages}
            />
          ) : (
            <div className="folder-detail__empty">No images in this folder.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderList;
